import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Copy, Check, Download, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { DOCS, findDoc } from "../docs";

// /documentacao e /documentacao/:slug — documentos internos (fonte: backend/docs/*.md).
// "Copiar Markdown" entrega o texto cru para colar numa IA; "Baixar" salva o .md.

function slugify(text) {
    return String(text).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function textOf(children) {
    if (typeof children === "string") return children;
    if (Array.isArray(children)) return children.map(textOf).join("");
    if (children && children.props) return textOf(children.props.children);
    return "";
}

function CodeBlock({ children, className }) {
    const [copied, setCopied] = useState(false);
    const code = textOf(children).replace(/\n$/, "");
    const lang = (className || "").replace("language-", "");
    async function copiar() {
        try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { toast.error("Não foi possível copiar"); }
    }
    return (
        <div className="relative group my-4">
            <div className="absolute right-2 top-2 flex items-center gap-2">
                {lang && <span className="text-[10px] uppercase tracking-wider text-ink-4">{lang}</span>}
                <button onClick={copiar} className="rounded-lg bg-white/[0.06] hover:bg-white/[0.1] p-1.5 text-ink-3 transition-colors" title="Copiar">
                    {copied ? <Check className="h-3.5 w-3.5 text-positive-400" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
            </div>
            <pre className="overflow-x-auto rounded-xl bg-black/40 ring-1 ring-white/[0.06] p-4 text-[13px] leading-relaxed text-ink-2">
                <code>{code}</code>
            </pre>
        </div>
    );
}

const MD = {
    h1: ({ children }) => <h1 className="display-2 mb-3 mt-2">{children}</h1>,
    h2: ({ children }) => <h2 id={slugify(textOf(children))} className="heading-1 mt-10 mb-4 pt-6 border-t border-white/[0.06] scroll-mt-24">{children}</h2>,
    h3: ({ children }) => <h3 id={slugify(textOf(children))} className="heading-3 mt-7 mb-3 scroll-mt-24">{children}</h3>,
    p: ({ children }) => <p className="text-ink-2 text-[15px] leading-7 my-3">{children}</p>,
    a: ({ href, children }) => <a href={href} className="text-brand-300 underline underline-offset-2 hover:text-brand-200" target={/^https?:/.test(href || "") ? "_blank" : undefined} rel="noreferrer">{children}</a>,
    ul: ({ children }) => <ul className="my-3 space-y-1.5 pl-5 list-disc marker:text-brand-400 text-ink-2 text-[15px] leading-7">{children}</ul>,
    ol: ({ children }) => <ol className="my-3 space-y-1.5 pl-5 list-decimal marker:text-brand-400 text-ink-2 text-[15px] leading-7">{children}</ol>,
    li: ({ children }) => <li className="pl-1">{children}</li>,
    strong: ({ children }) => <strong className="text-ink-1 font-semibold">{children}</strong>,
    em: ({ children }) => <em className="text-ink-2 italic">{children}</em>,
    hr: () => null,
    blockquote: ({ children }) => <blockquote className="my-4 rounded-xl border-l-2 border-brand-400 bg-brand-500/[0.06] px-4 py-3 text-ink-2 [&>p]:my-1">{children}</blockquote>,
    code: ({ inline, className, children, node, ...rest }) => {
        const isBlock = className?.startsWith("language-") || textOf(children).includes("\n");
        if (isBlock) return <CodeBlock className={className}>{children}</CodeBlock>;
        return <code className="rounded-md bg-white/[0.07] px-1.5 py-0.5 text-[13px] text-brand-200 font-mono" {...rest}>{children}</code>;
    },
    pre: ({ children }) => <>{children}</>,
    table: ({ children }) => <div className="my-4 overflow-x-auto rounded-xl ring-1 ring-white/[0.06]"><table className="w-full text-sm">{children}</table></div>,
    thead: ({ children }) => <thead className="bg-white/[0.04] text-left">{children}</thead>,
    th: ({ children }) => <th className="px-3 py-2 label-overline text-ink-3 font-medium">{children}</th>,
    td: ({ children }) => <td className="px-3 py-2 align-top text-ink-2 border-t border-white/[0.05] leading-6">{children}</td>,
};

function extrairSumario(md) {
    return md.split("\n").filter((l) => /^##\s/.test(l)).map((l) => {
        const t = l.replace(/^##\s+/, "").replace(/`/g, "").trim();
        return { titulo: t, id: slugify(t) };
    });
}

export default function Documentacao() {
    const { isOpen } = useContext(SidebarContext);
    const { slug } = useParams();
    const navigate = useNavigate();
    const doc = findDoc(slug) || DOCS[0];
    const [copied, setCopied] = useState(false);
    const sumario = useMemo(() => extrairSumario(doc.conteudo), [doc]);

    useEffect(() => { if (!slug && DOCS[0]) navigate(`/documentacao/${DOCS[0].slug}`, { replace: true }); }, [slug]);
    useEffect(() => { window.scrollTo({ top: 0 }); }, [doc.slug]);

    async function copiarMarkdown() {
        try { await navigator.clipboard.writeText(doc.conteudo); setCopied(true); toast.success("Markdown copiado — cole na IA"); setTimeout(() => setCopied(false), 1500); }
        catch { toast.error("Não foi possível copiar"); }
    }
    function baixar() {
        const blob = new Blob([doc.conteudo], { type: "text/markdown;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = doc.arquivo.split("/").pop();
        a.click();
        URL.revokeObjectURL(a.href);
    }

    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>
                <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto">
                    <header className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in">
                        <div className="space-y-1">
                            <span className="label-overline text-brand-300/80">{doc.categoria}</span>
                            <h1 className="display-2 flex items-center gap-3">
                                Documentação
                                <BookOpen className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
                            </h1>
                            <p className="text-ink-3 text-sm">Padrões do Metriqa para você e para a IA que monta suas páginas e fluxos.</p>
                        </div>
                        <div className="surface-card flex items-center gap-2 p-1.5">
                            <button onClick={copiarMarkdown} className="btn-primary flex items-center gap-2 text-sm">
                                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copiar Markdown
                            </button>
                            <button onClick={baixar} className="btn-ghost flex items-center gap-2 text-sm" title={doc.arquivo}>
                                <Download className="h-4 w-4" /> Baixar .md
                            </button>
                        </div>
                    </header>

                    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
                        <aside className="space-y-4 lg:sticky lg:top-6">
                            <div className="surface-card p-3">
                                <p className="label-overline text-ink-4 px-2 mb-2">Documentos</p>
                                <ul className="space-y-1">
                                    {DOCS.map((d) => (
                                        <li key={d.slug}>
                                            <button onClick={() => navigate(`/documentacao/${d.slug}`)}
                                                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors flex items-start gap-2 ${d.slug === doc.slug ? "bg-brand-500/15 text-brand-200 ring-1 ring-brand-500/20" : "text-ink-2 hover:bg-white/[0.05]"}`}>
                                                <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                                                <span>
                                                    <span className="block font-medium">{d.titulo}</span>
                                                    <span className="block text-xs text-ink-4">{d.categoria}</span>
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {sumario.length > 0 && (
                                <div className="surface-card p-3 hidden lg:block">
                                    <p className="label-overline text-ink-4 px-2 mb-2">Neste documento</p>
                                    <ul className="space-y-0.5">
                                        {sumario.map((s) => (
                                            <li key={s.id}>
                                                <a href={`#${s.id}`} className="block rounded-lg px-3 py-1.5 text-[13px] text-ink-3 hover:text-ink-1 hover:bg-white/[0.05] transition-colors">{s.titulo}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </aside>

                        <article className="surface-card p-6 sm:p-8 min-w-0">
                            <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-4">
                                <span>Fonte: <code className="font-mono">{doc.arquivo}</code></span>
                                <button onClick={baixar} className="inline-flex items-center gap-1.5 text-brand-300 hover:text-brand-200 underline underline-offset-2">
                                    <Download className="h-3.5 w-3.5" /> Baixar este documento (.md) para enviar à IA
                                </button>
                                <button onClick={copiarMarkdown} className="inline-flex items-center gap-1.5 text-brand-300 hover:text-brand-200 underline underline-offset-2">
                                    <Copy className="h-3.5 w-3.5" /> Copiar o texto
                                </button>
                            </div>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{doc.conteudo}</ReactMarkdown>
                        </article>
                    </div>
                </div>
            </div>
        </div>
    );
}
